import React, { useMemo } from 'react';
import { useJobs } from '../services/JobContext';
import { useNavigate } from 'react-router-dom';
import {
  format,
  isToday,
  isTomorrow,
  isThisWeek,
  addDays,
  startOfDay,
  isSameDay,
  differenceInCalendarDays,
  formatDistanceToNow,
} from 'date-fns';
import { SkeletonDashboard } from './SkeletonLoader';

/* ---------- helpers ---------- */

const isFinalFlagTrue = (v) => v === true || v === 'true' || v === 1 || v === '1';

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/* ---------- component ---------- */

const Dashboard = () => {
  const { jobs, interviews, getUpcomingInterviews, loading } = useJobs();
  const navigate = useNavigate();

  /* ---- Core derived values ---- */

  const upcomingInterviews = useMemo(
    () =>
      getUpcomingInterviews().sort(
        (a, b) => new Date(a.dateTime) - new Date(b.dateTime)
      ),
    [getUpcomingInterviews]
  );

  const stats = useMemo(() => {
    const totalJobs = jobs.length;

    const waiting = jobs.filter((j) => j.status === 'waiting').length;
    const completed = jobs.filter((j) => j.status === 'completed').length;
    const rejected = jobs.filter((j) => j.status === 'rejected').length;
    const inProgress = jobs.filter((j) => {
      if (j.status === 'completed' || j.status === 'rejected') return false;
      return interviews.some((i) => i.jobId === j.id);
    }).length;

    // Offer-awaiting detection (strict — final-round converted)
    const offerAwaiting = jobs.filter((j) => {
      if (j.status === 'completed' || j.status === 'rejected') return false;
      return interviews.some(
        (i) =>
          i.jobId === j.id &&
          isFinalFlagTrue(i.isFinalRound) &&
          i.status === 'converted'
      );
    }).length;

    const activePipeline = jobs.filter(
      (j) => j.status !== 'completed' && j.status !== 'rejected'
    ).length;

    // Conversion rate of interviews that were concluded
    const concluded = interviews.filter((i) =>
      ['converted', 'rejected'].includes(i.status)
    );
    const converted = concluded.filter((i) => i.status === 'converted').length;
    const conversionRate = concluded.length
      ? (converted / concluded.length) * 100
      : 0;

    // Upcoming buckets
    const todayCount = upcomingInterviews.filter((i) =>
      isToday(new Date(i.dateTime))
    ).length;
    const tomorrowCount = upcomingInterviews.filter((i) =>
      isTomorrow(new Date(i.dateTime))
    ).length;
    const weekCount = upcomingInterviews.filter((i) =>
      isThisWeek(new Date(i.dateTime), { weekStartsOn: 1 })
    ).length;

    return {
      totalJobs,
      waiting,
      completed,
      rejected,
      inProgress,
      offerAwaiting,
      activePipeline,
      conversionRate,
      concluded: concluded.length,
      converted,
      todayCount,
      tomorrowCount,
      weekCount,
    };
  }, [jobs, interviews, upcomingInterviews]);

  /* ---- Funnel segments (pipeline breakdown) ---- */

  const funnel = useMemo(() => {
    const segments = [
      { key: 'waiting',   label: 'Waiting',        value: stats.waiting,       cls: 'waiting' },
      { key: 'progress',  label: 'In progress',    value: stats.inProgress,    cls: 'progress' },
      { key: 'offer',     label: 'Offer awaiting', value: stats.offerAwaiting, cls: 'offer' },
      { key: 'completed', label: 'Completed',      value: stats.completed,     cls: 'completed' },
      { key: 'rejected',  label: 'Rejected',       value: stats.rejected,      cls: 'rejected' },
    ];
    const total = segments.reduce((acc, s) => acc + s.value, 0);
    return { segments, total };
  }, [stats]);

  /* ---- Weekly schedule strip (next 7 days) ---- */

  const weekStrip = useMemo(() => {
    const today = startOfDay(new Date());
    const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));
    const counts = days.map(
      (day) =>
        interviews.filter((i) => {
          const d = safeDate(i.dateTime);
          return d && isSameDay(d, day);
        }).length
    );
    const maxCount = Math.max(1, ...counts);
    return { days, counts, maxCount };
  }, [interviews]);

  /* ---- Activity feed ---- */

  const activity = useMemo(() => {
    const events = [];
    jobs.forEach((j) => {
      const d = safeDate(j.createdAt);
      if (d) {
        events.push({
          id: `job-${j.id}`,
          type: 'job_added',
          date: d,
          job: j,
          text: (
            <>
              New job added · <strong>{j.companyName}</strong> — {j.position}
            </>
          ),
        });
      }
    });
    interviews.forEach((i) => {
      const created = safeDate(i.createdAt);
      const job = jobs.find((j) => j.id === i.jobId);
      if (created) {
        events.push({
          id: `iv-created-${i.id}`,
          type: 'interview_scheduled',
          date: created,
          job,
          interview: i,
          text: (
            <>
              Interview scheduled · Round {i.round}
              {job ? <> at <strong>{job.companyName}</strong></> : null}
            </>
          ),
        });
      }
      if (i.feedback) {
        // Use created as proxy when feedbackDate is missing
        const feedbackDate = created;
        if (feedbackDate) {
          events.push({
            id: `iv-fb-${i.id}`,
            type: 'feedback_recorded',
            date: feedbackDate,
            job,
            interview: i,
            text: (
              <>
                Feedback recorded · {i.status === 'converted' ? 'Converted' : i.status === 'rejected' ? 'Rejected' : 'Updated'} · Round {i.round}
                {job ? <> at <strong>{job.companyName}</strong></> : null}
              </>
            ),
          });
        }
      }
    });
    return events
      .sort((a, b) => b.date - a.date)
      .slice(0, 6);
  }, [jobs, interviews]);

  if (loading) return <SkeletonDashboard />;

  /* ---------- Render ---------- */

  return (
    <div className="dashboard">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Dashboard</h1>
          <p>A live pulse across your pipeline, interviews, and activity.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/kanban')}>
            Open board
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/schedule')}>
            Schedule interview
          </button>
        </div>
      </div>

      {/* ---------- KPI row ---------- */}
      <div className="dashboard-kpis">
        <button
          type="button"
          className="dash-kpi dash-kpi--pipeline"
          onClick={() => navigate('/kanban')}
        >
          <span className="dash-kpi__label">Active pipeline</span>
          <span className="dash-kpi__value">{stats.activePipeline}</span>
          <span className="dash-kpi__meta">
            of {stats.totalJobs} total · {stats.inProgress} interviewing
          </span>
        </button>

        <button
          type="button"
          className="dash-kpi dash-kpi--week"
          onClick={() => navigate('/schedule')}
        >
          <span className="dash-kpi__label">This week</span>
          <span className="dash-kpi__value">{stats.weekCount}</span>
          <span className="dash-kpi__meta">
            {stats.todayCount} today · {stats.tomorrowCount} tomorrow
          </span>
        </button>

        <button
          type="button"
          className="dash-kpi dash-kpi--conv"
          onClick={() => navigate('/insights')}
        >
          <span className="dash-kpi__label">Conversion rate</span>
          <span className="dash-kpi__value">
            {stats.conversionRate.toFixed(1)}
            <span className="dash-kpi__unit">%</span>
          </span>
          <span className="dash-kpi__meta">
            {stats.converted} converted of {stats.concluded} concluded
          </span>
        </button>

        <button
          type="button"
          className={`dash-kpi dash-kpi--offer ${
            stats.offerAwaiting > 0 ? 'is-active' : ''
          }`}
          onClick={() => navigate('/kanban')}
        >
          <span className="dash-kpi__label">Offer awaiting</span>
          <span className="dash-kpi__value">{stats.offerAwaiting}</span>
          <span className="dash-kpi__meta">
            {stats.offerAwaiting > 0
              ? 'Needs final decision'
              : 'No pending offers'}
          </span>
          {stats.offerAwaiting > 0 && <span className="dash-kpi__pulse" aria-hidden />}
        </button>
      </div>

      {/* ---------- Pipeline funnel + Weekly strip ---------- */}
      <div className="dashboard-grid dashboard-grid--2col">
        <section className="card dash-section">
          <div className="dash-section__header">
            <h3>Pipeline distribution</h3>
            <span className="dash-section__meta">
              {funnel.total} job{funnel.total === 1 ? '' : 's'}
            </span>
          </div>
          {funnel.total === 0 ? (
            <EmptyInline text="No jobs in the pipeline yet." />
          ) : (
            <>
              <div className="dash-funnel" role="img" aria-label="Pipeline distribution">
                {funnel.segments
                  .filter((s) => s.value > 0)
                  .map((s) => {
                    const pct = (s.value / funnel.total) * 100;
                    return (
                      <div
                        key={s.key}
                        className={`dash-funnel__seg dash-funnel__seg--${s.cls}`}
                        style={{ width: `${pct}%` }}
                        title={`${s.label}: ${s.value} (${pct.toFixed(1)}%)`}
                      >
                        {pct >= 10 && <span>{s.value}</span>}
                      </div>
                    );
                  })}
              </div>
              <ul className="dash-funnel__legend">
                {funnel.segments.map((s) => (
                  <li key={s.key} className="dash-funnel__legend-item">
                    <span
                      className={`dash-funnel__dot dash-funnel__dot--${s.cls}`}
                    />
                    <span className="dash-funnel__legend-label">{s.label}</span>
                    <span className="dash-funnel__legend-value">{s.value}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="card dash-section">
          <div className="dash-section__header">
            <h3>Next 7 days</h3>
            <span className="dash-section__meta">
              {weekStrip.counts.reduce((a, b) => a + b, 0)} interviews
            </span>
          </div>
          <div className="dash-week">
            {weekStrip.days.map((day, i) => {
              const count = weekStrip.counts[i];
              const height = count === 0 ? 6 : (count / weekStrip.maxCount) * 100;
              const isTodayFlag = isToday(day);
              return (
                <div
                  key={i}
                  className={`dash-week__col ${isTodayFlag ? 'is-today' : ''} ${
                    count === 0 ? 'is-empty' : ''
                  }`}
                  title={`${format(day, 'EEE, MMM d')} — ${count} interview${count === 1 ? '' : 's'}`}
                >
                  <div className="dash-week__bar-wrap">
                    <div
                      className="dash-week__bar"
                      style={{ height: `${height}%` }}
                    >
                      {count > 0 && (
                        <span className="dash-week__bar-value">{count}</span>
                      )}
                    </div>
                  </div>
                  <div className="dash-week__day">
                    {isTodayFlag ? 'Today' : format(day, 'EEE')}
                  </div>
                  <div className="dash-week__date">{format(day, 'MMM d')}</div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ---------- Upcoming interviews + Activity feed ---------- */}
      <div className="dashboard-grid dashboard-grid--2col">
        <section className="card dash-section">
          <div className="dash-section__header">
            <h3>Upcoming interviews</h3>
            <button
              className="dash-section__link"
              onClick={() => navigate('/schedule')}
            >
              View all →
            </button>
          </div>
          {upcomingInterviews.length === 0 ? (
            <EmptyInline text="No upcoming interviews scheduled." />
          ) : (
            <ul className="dash-list">
              {upcomingInterviews.slice(0, 5).map((interview) => {
                const job = jobs.find((j) => j.id === interview.jobId);
                const d = safeDate(interview.dateTime);
                if (!d) return null;
                const diff = differenceInCalendarDays(d, new Date());
                const whenLabel =
                  diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : format(d, 'MMM d');
                return (
                  <li
                    key={interview.id}
                    className="dash-list__item dash-upcoming"
                    onClick={() => navigate('/kanban')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/kanban');
                      }
                    }}
                  >
                    <div className="dash-upcoming__when">
                      <span className="dash-upcoming__when-day">{whenLabel}</span>
                      <span className="dash-upcoming__when-time">
                        {format(d, 'hh:mm a')}
                      </span>
                    </div>
                    <div className="dash-upcoming__body">
                      <div className="dash-upcoming__title">
                        {job?.companyName || 'Unknown company'}
                      </div>
                      <div className="dash-upcoming__meta">
                        {job?.position || '—'} · Round {interview.round} ·{' '}
                        {interview.interviewType}
                      </div>
                    </div>
                    <span
                      className={`status-badge status-${interview.status || 'waiting'}`}
                    >
                      {interview.status || 'waiting'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="card dash-section">
          <div className="dash-section__header">
            <h3>Recent activity</h3>
          </div>
          {activity.length === 0 ? (
            <EmptyInline text="Activity will show up here as you work." />
          ) : (
            <ul className="dash-activity">
              {activity.map((ev) => (
                <li
                  key={ev.id}
                  className={`dash-activity__item dash-activity__item--${ev.type}`}
                >
                  <span className="dash-activity__marker" aria-hidden />
                  <div className="dash-activity__body">
                    <div className="dash-activity__text">{ev.text}</div>
                    <div className="dash-activity__time">
                      {formatDistanceToNow(ev.date, { addSuffix: true })}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

/* ---------- Tiny subcomponents ---------- */

const EmptyInline = ({ text }) => (
  <div className="dash-empty-inline">{text}</div>
);

export default Dashboard;
